import Button from "@components/buttons/Button"
import DeviceMenu, { DeviceMenuProductProps } from "@components/DeviceMenu"
import DiscountBlock from "@components/DiscountBlock"
import LanguageSelector from "@components/LanguageSelector"
import GlobalDataContext from "@components/Layout/GlobalDataContext"
import OutOfStockModal from "@components/Modal/OutOfStockModal/hoc/withCustomer"
import RequestReceivedModal from "@components/Modal/RequestReceivedModal"
import ProductImages from "@components/ProductImages"
import ProductTitle from "@components/ProductTitle"
import SizeSelector from "@components/SizeSelector"
import Stars from "@components/Stars"
import UpsellBlock from "@components/UpsellBlock"
import breakpoints from "@constants/breakpoints"
import colors from "@constants/colors"
import { subtextStyles } from "@constants/typography"
import useElevarEvents, { ElevarProductProps } from "@hooks/useElevarEvents"
import useEnhancedEcommerceEvents, {
  GTMProductProps,
} from "@hooks/useEnhancedEcommerceEvents"
import useScrollHandler from "@hooks/useScrollHandler"
import { formatContentfulVariant, getIn } from "@utils/index"
import { DesktopOnly, MobileAndTabletOnly } from "@utils/MediaQuery"
import ShippingBlock from "@views/common/ProductDetails/ShippingBlock"
import React from "react"
import styled from "styled-components"

import ProductForm from "./ProductForm"

const Container = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  padding-top: 43px;
  background-color: ${colors.offWhite};
  border-bottom: 1px solid ${colors.dividerLineGray};

  @media ${breakpoints.tablet} {
    padding-top: 92px;
  }

  @media ${breakpoints.laptop} {
    flex-direction: row;
    padding: 58px calc(1 / 12 * 100vw) 120px;
  }
`

const Images = styled.div`
  display: flex;
  flex-direction: column;
  margin: 0;

  @media ${breakpoints.laptop} {
    width: calc(6 / 12 * 100vw);
  }
`

const getFormWrapperHeight = ({
  height,
  shortenHeader,
  showPromoBanner,
}: {
  height: string
  shortenHeader: boolean
  showPromoBanner?: boolean
}) => {
  const headerHeight = shortenHeader ? 64 : 88
  const bannerHeight = showPromoBanner ? 36 : 0
  return `calc(${height} - 58px - ${headerHeight}px - ${bannerHeight}px)`
}
const FormWrapper = styled.div<{
  shortenHeader: boolean
  showPromoBanner?: boolean
}>`
  @media ${breakpoints.laptop} {
    display: flex;
    position: fixed;
    right: calc(5 / 12 * 100vw);
    height: ${(props) => getFormWrapperHeight({ ...props, height: "100vh" })};
    align-items: center;

    &.fixed--bottom {
      position: absolute;
      top: 58px;
      align-items: flex-end;
    }
  }
`

const Form = styled.div`
  display: flex;
  flex-direction: column;
  margin: 32px 20px 72px;

  @media ${breakpoints.tablet} {
    margin: 80px calc(1 / 12 * 100vw) 120px;
  }

  @media ${breakpoints.laptop} {
    position: absolute;
    top: 0;
    width: calc(3 / 12 * 100vw);
    margin: 0;
    margin-left: calc(1 / 12 * 100vw);
  }
`

const ProductTitleWrapper = styled.div`
  margin: 0 20px 40px;

  @media ${breakpoints.tablet} {
    margin: 0 calc(1 / 12 * 100vw) 56px;
  }

  @media ${breakpoints.laptop} {
    margin: 0 0 32px 0;
  }
`

const CartButtonAndTextWrapper = styled.div`
  display: flex;
  flex-direction: column;

  @media ${breakpoints.tablet} {
    margin-top: 54px;
    align-items: center;
    justify-content: space-between;
  }
`

const ActionButtonWrapper = styled.div`
  @media ${breakpoints.tablet} {
    width: 100%;
  }
`

const ActionButton = styled(Button)`
  margin-top: 24px;

  @media ${breakpoints.mobileOnly} {
    height: 56px;
  }

  @media ${breakpoints.tablet} {
    margin-top: 0;
  }

  @media ${breakpoints.laptop} {
    margin-top: 32px;
  }
`

const RatingsRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  margin-top: 20px;

  &:hover {
    cursor: pointer;
  }
`

const Ratings = styled(Stars)`
  margin-right: 8px;
`

const RatingsText = styled.div`
  ${subtextStyles}
  color: ${colors.mediumDarkGray};
`

const SkuUpcContainer = styled.div`
  display: flex;
  margin-top: 8px;
`

const SkuUpcNumber = styled.div`
  ${subtextStyles}
  margin-right: 20px;
  color: ${colors.mediumGray};
`

const DiscountAndUpsellBlockWrapper = styled.div`
  width: 100%;
  margin-top: 32px;
`

interface ProductBlockProps {
  addDiscountedProductToCart: any
  addToCart: () => void
  automaticDiscounts: any
  avgRating: number
  bogoTypeByIndex?: { [key: number]: "X" | "Y" }
  brand: string
  currentVariant: VariantProps
  customerLocationFetched: boolean
  customerZipCode: string
  deliveryText?: React.ReactNode
  device: string
  deviceAnchorLabel: string
  discountProducts: any
  discountProductsIdMap: any
  enableFenixEstimates: boolean
  fallbackVariant: VariantProps
  globalData: any
  headerRef: any
  hideDeviceSelector?: boolean
  hideZipCodeForm: boolean
  imageGalleryMuxVideos?: any
  images: Array<string>
  invalidShippingData: boolean
  isAvailable: boolean
  lifestyleMuxVideos?: any
  loadingInventory: boolean
  multipleDevices?: boolean
  notAvailable?: boolean
  notAvailableButtonText?: string
  notAvailableInCountry: boolean
  notAvailableInCountryText?: string
  numReviews: number
  onClickRatings?: (type: "desktop" | "mobile") => void
  onUpdateZipCode?: (zipCode: string) => void
  openProductSelector: (i, id) => void
  openVariantSelector: () => void
  outOfStockButtonText?: string
  price: string
  productName: string
  productsInSeries: Array<DeviceMenuProductProps>
  retailPrice?: string
  setActiveVariant: (variantIndex: number) => void
  shippingDataLoaded: boolean
  shippingText: React.ReactNode
  showDiscountBlock: boolean
  upsellCallout?: string
  upsellProduct?: any
  useDeviceAnchor: boolean
  variants: VariantProps[]
}

interface VariantProps {
  compareAtPrice?: string
  contentfulVariantId: string
  hexString?: string
  images: Array<string>
  language?: string
  marketPrices: any
  name: string
  price: string
  size?: string
  sku: string
  swatchImage?: string
  upc?: string
  variantId: string
}

const ProductBlock = (props: ProductBlockProps) => {
  const containerRef = React.useRef<HTMLDivElement>(null)
  const formContainerRef = React.useRef<HTMLDivElement>(null)
  const formRef = React.useRef<HTMLDivElement>(null)

  const [shortenHeader, setShortenHeader] = React.useState(false)
  const { showPromoBanner }: any = React.useContext(GlobalDataContext)

  const scrollHandler = () => {
    const html = document.getElementsByTagName("html")
    const shouldShortenHeader =
      html && html[0] && html[0].getBoundingClientRect().top < -40
    if (shouldShortenHeader && !shortenHeader) {
      setShortenHeader(true)
    } else if (!shouldShortenHeader && shortenHeader) {
      setShortenHeader(false)
    }
  }

  useScrollHandler({ onScroll: scrollHandler })

  const [deviceMenuIsOpen, setDeviceMenuIsOpen] = React.useState(false)

  const closeDeviceMenu = () => {
    setDeviceMenuIsOpen(false)
  }

  const [languageSelectorIsOpen, setLanguageSelectorIsOpen] =
    React.useState(false)
  const [languageSelectorData, setLanguageSelectorData] = React.useState([])

  const openLanguageSelector = (productLanguages) => {
    setLanguageSelectorData(productLanguages)
    setLanguageSelectorIsOpen(true)
  }

  const closeLanguageSelector = () => {
    setLanguageSelectorIsOpen(false)
  }

  const [sizeSelectorIsOpen, setSizeSelectorIsOpen] = React.useState(false)
  const [sizeSelectorData, setSizeSelectorData] = React.useState([])

  const openSizeSelector = (productSizes) => {
    setSizeSelectorData(productSizes)
    setSizeSelectorIsOpen(true)
  }

  const closeSizeSelector = () => {
    setSizeSelectorIsOpen(false)
  }

  const [showOutOfStockModal, setShowOutOfStockModal] =
    React.useState<boolean>(false)

  const openOutOfStockModal = () => {
    setShowOutOfStockModal(true)
  }

  const closeOutOfStockModal = () => {
    setShowOutOfStockModal(false)
  }

  const [showRequestReceivedModal, setshowRequestReceivedModal] =
    React.useState<boolean>(false)

  const openRequestReceivedModal = () => {
    setshowRequestReceivedModal(true)
  }

  const closeRequestReceivedModal = () => {
    setshowRequestReceivedModal(false)
  }

  const onSubmitOutOfStockSuccess = () => {
    openRequestReceivedModal()
    closeOutOfStockModal()
  }

  const RenderedActionButton = () => {
    if (props.notAvailableInCountry) {
      return (
        <ActionButton
          className="notAvailable"
          disabled
          hideDash
          text={
            props.notAvailableInCountryText ||
            "This product is not available in your country"
          }
        />
      )
    } else if (props.notAvailable && props.notAvailableButtonText) {
      return (
        <ActionButton
          className="notAvailable"
          disabled
          hideDash
          text={props.notAvailableButtonText}
        />
      )
    } else if (props.loadingInventory) {
      return <ActionButton className="addToCart" disabled text={"Loading"} />
    } else if (props.isAvailable) {
      return (
        <ActionButton
          className="addToCart"
          disabled={!props.isAvailable}
          hideDash={!props.isAvailable}
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
            props.addToCart()
          }}
          text={"Add to Cart"}
        />
      )
    } else if (props.outOfStockButtonText) {
      return (
        <ActionButton
          className="outOfStock"
          disabled
          hideDash
          text={props.outOfStockButtonText}
        />
      )
    } else {
      return (
        <ActionButton
          className="outOfStock"
          onClick={openOutOfStockModal}
          text={"Notify Me When Available"}
        />
      )
    }
  }

  const outOfStockMessage = getIn(props, "currentVariant.outOfStockMessage")

  const { onAddProductToCart } = useEnhancedEcommerceEvents()
  const { elevarOnAddToCart } = useElevarEvents()

  const handleOnDiscountClick = (i) => {
    if (
      props.discountProducts[i] &&
      props.discountProducts[i].length === 1 &&
      props.discountProducts[i][0] &&
      props.discountProducts[i][0].variants &&
      props.discountProducts[i][0].variants.length === 1
    ) {
      const p = props.discountProducts[i][0]
      const v = formatContentfulVariant(
        props.discountProducts[i][0].variants[0]
      ) as any
      const imageSrc = v.images[0] || ""
      const [cleanedSrc] = imageSrc.split("?")

      props.addDiscountedProductToCart({
        cartProduct: {
          brand: p.brand?.name,
          color: v.color,
          compareAtPrice: v.compareAtPrice,
          contentfulProductId: p.contentful_id,
          contentfulVariantId: v.contentfulVariantId,
          customPrint: p.customPrint,
          device: p.device?.name,
          deviceType: p.device?.type?.name,
          image: `https:${cleanedSrc}`,
          language: p.language,
          marketPrices: v.marketPrices,
          price: v.price,
          productName: p.productName,
          productSlug: p.slug,
          productSubType: p.productSubType?.slug,
          quantity: 1,
          shopifyProductId: p.shopifyProduct?.shopifyProductId,
          shopifyVariantId: v.variantId,
          size: v.size,
          sku: v.sku,
          variantDevice: v.deviceName,
          variantName: v.name,
        },
      })

      const elevarProduct: ElevarProductProps = {
        brand: "Urban Armor Gear",
        device: p.device?.name,
        deviceType: p.device?.type?.name,
        image: `https:${cleanedSrc}`,
        list: "upsell",
        price: v.price,
        productId: p.shopifyProduct?.shopifyProductId,
        productName: p.productName,
        quantity: "1",
        sku: v.sku,
        variantId: v.variantId,
        variantName: v.name,
      }

      elevarOnAddToCart(elevarProduct, "upsell")

      const gtmProduct: GTMProductProps = {
        brand: p.brand.name,
        device: p.device.name,
        deviceType: p.device?.type?.name,
        price: v.price,
        productId: p.shopifyProduct?.shopifyProductId,
        productName: p.productName,
        quantity: 1,
        sku: v.sku,
        variantName: v.name,
      }

      onAddProductToCart(gtmProduct)
    } else {
      props.openProductSelector(i, props.discountProductsIdMap[i])
    }
  }

  const handleOnUpsellClick = () => {
    if (props.upsellProduct?.variants?.length > 1) {
      props.openVariantSelector()
    } else {
      props.upsellProduct.variants[0].onAddToCart(1)
    }
  }

  return (
    <Container ref={containerRef}>
      <DeviceMenu
        globalData={props.globalData}
        isOpen={deviceMenuIsOpen}
        onRequestClose={closeDeviceMenu}
        products={props.productsInSeries}
      />
      <LanguageSelector
        isOpen={languageSelectorIsOpen}
        languages={languageSelectorData}
        onRequestClose={closeLanguageSelector}
      />
      <SizeSelector
        isOpen={sizeSelectorIsOpen}
        onRequestClose={closeSizeSelector}
        sizes={sizeSelectorData}
      />
      <MobileAndTabletOnly>
        <ProductTitleWrapper>
          <ProductTitle
            price={props.price}
            productName={props.productName}
            retailPrice={props.retailPrice}
          />
          <SkuUpcContainer>
            <SkuUpcNumber>SKU: {props.currentVariant.sku}</SkuUpcNumber>
            {props.currentVariant.upc && (
              <SkuUpcNumber>UPC: {props.currentVariant.upc}</SkuUpcNumber>
            )}
          </SkuUpcContainer>
          <RatingsRow onClick={() => props.onClickRatings?.("mobile")}>
            <Ratings stars={Math.round(props.avgRating)} />
            <RatingsText>{props.numReviews} Reviews</RatingsText>
          </RatingsRow>
        </ProductTitleWrapper>
      </MobileAndTabletOnly>
      <Images>
        <ProductImages
          currentVariant={props.currentVariant || props.fallbackVariant}
          imageGalleryMuxVideos={props.imageGalleryMuxVideos}
          images={props.images}
          lifestyleMuxVideos={props.lifestyleMuxVideos}
        />
      </Images>
      <FormWrapper
        className="fixed--bottom"
        ref={formContainerRef}
        shortenHeader={shortenHeader}
        showPromoBanner={showPromoBanner}
      >
        <Form ref={formRef}>
          <DesktopOnly>
            <ProductTitleWrapper>
              <ProductTitle
                price={props.price}
                productName={props.productName}
                retailPrice={props.retailPrice}
              />
              <SkuUpcContainer>
                <SkuUpcNumber>SKU: {props.currentVariant.sku}</SkuUpcNumber>
                {props.currentVariant.upc && (
                  <SkuUpcNumber>UPC: {props.currentVariant.upc}</SkuUpcNumber>
                )}
              </SkuUpcContainer>
              <RatingsRow onClick={() => props.onClickRatings?.("desktop")}>
                <Ratings stars={Math.round(props.avgRating)} />
                <RatingsText>{props.numReviews} Reviews</RatingsText>
              </RatingsRow>
            </ProductTitleWrapper>
          </DesktopOnly>
          <ProductForm
            brand={props.brand}
            currentVariant={props.currentVariant || props.fallbackVariant}
            device={props.device}
            deviceAnchorLabel={props.deviceAnchorLabel}
            hideDeviceSelector={props.hideDeviceSelector}
            multipleDevices={props.multipleDevices}
            onClickLanguageSelector={openLanguageSelector}
            onClickSizeSelector={openSizeSelector}
            onRequestCloseLanguageSelector={closeLanguageSelector}
            onRequestCloseSizeSelector={closeSizeSelector}
            onVariantChange={props.setActiveVariant}
            productsInSeries={props.productsInSeries}
            useDeviceAnchor={props.useDeviceAnchor}
            variants={props.variants}
          />
          <CartButtonAndTextWrapper>
            <ActionButtonWrapper ref={props.headerRef}>
              <RenderedActionButton />
            </ActionButtonWrapper>
            {!props.notAvailableInCountry && (
              <ShippingBlock
                customerLocationFetched={props.customerLocationFetched}
                customerZipCode={props.customerZipCode}
                deliveryText={props.deliveryText}
                enableFenixEstimates={props.enableFenixEstimates}
                hideZipCodeForm={props.hideZipCodeForm}
                invalidShippingData={props.invalidShippingData}
                onUpdateZipCode={props.onUpdateZipCode}
                outOfStockMessage={outOfStockMessage}
                shippingDataLoaded={props.shippingDataLoaded}
                shippingText={props.shippingText}
              />
            )}
            {!props.notAvailableInCountry &&
              props.showDiscountBlock &&
              props.automaticDiscounts.map((discount, i) => {
                const discountId = discount.id || ""
                const parsedDiscountId = discountId.replace(
                  "gid://shopify/DiscountAutomaticNode/",
                  ""
                )
                const discountConfig =
                  props?.globalData?.automaticDiscounts?.find(
                    ({ discountId }) => discountId === parsedDiscountId
                  )

                if (
                  !discountConfig ||
                  (props.discountProducts[i] &&
                    props.discountProducts[i].length === 0)
                ) {
                  return null
                }

                if (discountConfig && discountConfig.type === "flat discount") {
                  return null
                }

                const type = props.bogoTypeByIndex?.[i]

                return (
                  <DiscountAndUpsellBlockWrapper key={i}>
                    <DiscountBlock
                      buttonText={
                        props.discountProducts[i] &&
                        props.discountProducts[i].length === 1 &&
                        props.discountProducts[i][0].variants?.length === 1
                          ? "Add to Cart"
                          : "Add"
                      }
                      deal={discountConfig[`pdpMessage${type}`]}
                      image={
                        props.discountProducts[i] &&
                        props.discountProducts[i].length === 1
                          ? props.discountProducts[i][0].productImage
                          : discountConfig[
                              `select${type === "X" ? "Y" : "X"}Image`
                            ]?.file?.url || ""
                      }
                      onClick={() => {
                        handleOnDiscountClick(i)
                      }}
                      productName={
                        props.discountProducts[i] &&
                        props.discountProducts[i].length === 1
                          ? props.discountProducts[i][0].productName
                          : discountConfig[`select${type}Message`]
                      }
                      slug={
                        props.discountProducts[i] &&
                        props.discountProducts[i].length === 1
                          ? props.discountProducts[i][0].slug
                          : ""
                      }
                    />
                  </DiscountAndUpsellBlockWrapper>
                )
              })}
            {!props.notAvailableInCountry &&
              !props.showDiscountBlock &&
              props.upsellProduct && (
                <DiscountAndUpsellBlockWrapper>
                  <UpsellBlock
                    buttonText={
                      props.upsellProduct?.variants?.length > 1
                        ? "Add"
                        : "Add to Cart"
                    }
                    callout={props.upsellCallout!}
                    discountedPrice={props.upsellProduct.productCompareAtPrice}
                    image={props.upsellProduct.productImage}
                    onClick={handleOnUpsellClick}
                    price={props.upsellProduct.productPrice}
                    productName={props.upsellProduct.productName}
                    slug={props.upsellProduct.slug}
                  />
                </DiscountAndUpsellBlockWrapper>
              )}
          </CartButtonAndTextWrapper>
        </Form>
      </FormWrapper>
      <OutOfStockModal
        isOpen={showOutOfStockModal}
        onRequestClose={closeOutOfStockModal}
        showSuccess={onSubmitOutOfStockSuccess}
        variantId={props.currentVariant.variantId}
      />
      <RequestReceivedModal
        isOpen={showRequestReceivedModal}
        onRequestClose={closeRequestReceivedModal}
      />
    </Container>
  )
}

export default ProductBlock
